/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { Undefined } from '../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { CARD_DATA_MODAL_TYPE } from './CardModalConstsAndTypes';

import { isVodItem } from '../../../helpers/ui/item/metadata';
import { filterSections } from '../../../helpers/ui/metadata/Exclusion';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { type NETGEM_API_V8_AVENUE } from '../../../libs/netgemLibrary/v8/types/Avenue';
import { ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { GroupItemsBy, type NetgemApiV8WidgetItemClick } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import sendV8CustomStrategyRequest from '../../../redux/netgemApi/actions/v8/customStrategyRequest';
import RegularSection from '../../avenue/section/Section';
import './CardModalAvenue.css';
import SeriesSection from '../../avenue/sectionSeries/Section';

type DefaultProps = {
  +displayedSectionCount?: number | null,
};

type AvenuePropType = {
  ...DefaultProps,
  +cardData: CARD_DATA_MODAL_TYPE,
};

const CardModalAvenue = ({ cardData, displayedSectionCount }: AvenuePropType): React.Node => {
  const dispatch = useDispatch();

  const abortController = useRef<AbortController>(new AbortController());

  const [avenue, setAvenue] = useState<NETGEM_API_V8_AVENUE | null>(null);
  const [cardItem, setCardItem] = useState<NETGEM_API_V8_FEED_ITEM | null>(null);

  // Only contains a cleanup function
  useEffect(() => () => abortController.current.abort('Component CardModalAvenue will unmount'), []);

  const destroyAvenue = useCallback(() => {
    setAvenue(null);
    setCardItem(null);
  }, []);

  const loadAvenueFromUrlDefinition = useCallback(
    (urlDefinition: NETGEM_API_V8_URL_DEFINITION, item: NETGEM_API_V8_FEED_ITEM) => {
      const { signal } = abortController.current;

      dispatch(sendV8CustomStrategyRequest(urlDefinition, item, signal))
        .then((response) => {
          signal.throwIfAborted();

          setAvenue(filterSections(response));
        })
        .catch((error) => ignoreIfAborted(signal, error, destroyAvenue));
    },
    [destroyAvenue],
  );

  const loadAvenue = useCallback(() => {
    const {
      item,
      item: { type },
      seriesMetadata,
      urlDefinition,
    } = cardData;

    if (!urlDefinition) {
      return;
    }

    // Used to force treating episode of series with "program" type as a series
    const newCardItem: NETGEM_API_V8_FEED_ITEM =
      type === ItemType.Program && seriesMetadata !== undefined && seriesMetadata !== null
        ? {
            ...item,
            id: seriesMetadata.id,
            seriesId: seriesMetadata.id,
            type: ItemType.Series,
          }
        : item;

    setCardItem(newCardItem);
    loadAvenueFromUrlDefinition(urlDefinition, newCardItem);
  }, [cardData.item, cardData.seriesMetadata, cardData.urlDefinition, loadAvenueFromUrlDefinition]);

  useEffect(() => {
    loadAvenue();
  }, [cardData.item?.id, cardData.urlDefinition, loadAvenue]);

  const dataLoadedCallback = useCallback(() => {
    // Nothing to do at the moment
  }, []);

  const renderAvenue = useCallback((): Array<React.Node> | null => {
    const { seriesMetadata } = cardData;

    if (!avenue || !cardItem || (typeof displayedSectionCount === 'number' && displayedSectionCount === 0)) {
      return null;
    }

    const {
      id: itemId,
      selectedLocation: { id: selectedLocationId },
      seriesId,
    } = cardItem;

    const {
      sections,
      sections: { length },
    } = avenue;
    const sectionCount = typeof displayedSectionCount === 'number' ? Math.min(displayedSectionCount, length) : length;
    const sectionContainer: Array<React.Node> = [];

    for (let i: number = 0; i < sectionCount; ++i) {
      const { [i]: section } = sections;
      const { id, widgetConfig } = section;
      let groupItemsBy: ?GroupItemsBy | null = null;
      let onItemClick: Undefined<NetgemApiV8WidgetItemClick> = undefined;

      if (widgetConfig) {
        ({ groupItemsBy, onItemClick } = widgetConfig);
      }

      if (seriesId && groupItemsBy === GroupItemsBy.Program) {
        sectionContainer.push(
          <SeriesSection
            cardData={cardData}
            focusedLocationId={selectedLocationId}
            isVod={isVodItem(cardItem)}
            key={`section_${id}`}
            onItemClick={onItemClick}
            section={section}
            seriesId={seriesId}
            seriesMetadata={seriesMetadata}
          />,
        );
      } else {
        sectionContainer.push(
          <RegularSection avenueIndex={0} cardData={cardData} dataLoadedCallback={dataLoadedCallback} key={`section_${id}_${itemId}`} onItemClick={onItemClick} section={section} sectionIndex={i} />,
        );
      }
    }

    return sectionContainer;
  }, [avenue, cardData, cardItem, displayedSectionCount]);

  if (typeof displayedSectionCount === 'number' && displayedSectionCount === 0) {
    return null;
  }

  return <div className='cardAvenueView'>{renderAvenue()}</div>;
};

export default CardModalAvenue;
